import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useGetCreditBalance, useGetCreditTransactions } from '../api/credits';
import type { CreditTransaction, CreditTransactionsFilter } from '../api/credits';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import filterIcon from '../assets/images/filter.png';
import downloadIcon from '../assets/images/download.png';
import plusIcon from '../assets/images/plus.png';

const CreditPointsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setNavLinks } = useNavLinksContext();

  const {
    data: creditBalance,
    isLoading: isLoadingBalance,
  } = useGetCreditBalance();

  const [filters, setFilters] = useState<CreditTransactionsFilter>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
  } = useGetCreditTransactions({ filters });

  const transactions = transactionsData?.transactions ?? [];

  const filteredTransactions = transactions.filter((t) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.name.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/credit-points', label: 'Credit Points' },
      ],
    });
  }, [setNavLinks]);

  const handlePurchasePoints = () => {
    console.log('Navigate to purchase points page');
  };

  const handleExportTransactions = () => {
    console.log('Exporting transactions');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'credit':
        return 'bg-green-100 text-green-700';
      case 'debit':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'credit', label: 'Credit' },
    { value: 'debit', label: 'Debit' },
  ];

  const handleFilterTypeChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      type: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <div>
              <h3 className="text-2xl font-medium">Credit Points</h3>
              <p className="text-grey_dark mt-1">Manage your credit points and transaction history</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4 md:mt-0">
              <Button
                variant="filled-primary"
                onClick={handlePurchasePoints}
                className="flex items-center gap-2"
              >
                <img src={plusIcon} alt="Add" className="w-4 h-4" />
                Purchase Points
              </Button>

              <Button
                variant="bordered-primary"
                onClick={handleExportTransactions}
                className="flex items-center gap-2"
              >
                <img src={downloadIcon} alt="Download" className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>

          {/* Balance Card */}
          <div className="bg-gradient-to-r from-primary_variant to-primary rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center gap-4 mb-4 md:mb-0">
                <div>
                  <h4 className="text-lg font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Available Balance</h4>
                  <div className="flex items-baseline gap-2">
                    {isLoadingBalance ? (
                      <span className="text-4xl font-bold text-white">...</span>
                    ) : (
                      <span className="text-4xl font-bold text-white">
                        {(creditBalance ?? 0).toLocaleString()}
                      </span>
                    )}
                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>points</span>
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/credit/history')}
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:opacity-80 transition-opacity"
                >
                  View History
                </button>

                <button
                  onClick={() => setShowFilter(!showFilter)}
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:opacity-80 transition-opacity"
                >
                  <img src={filterIcon} alt="Filter" className="w-4 h-4 brightness-0 invert" />
                  Filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        {showFilter && (
          <div className="bg-light rounded-lg p-4 mb-4 border border-grey_light">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-grey_dark mb-2">Search</label>
                <Input
                  type="text"
                  attributes={{
                    placeholder: 'Search by name or description...',
                    value: searchQuery,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value),
                  }}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-grey_dark mb-2">Transaction Type</label>
                <Select
                  options={typeOptions}
                  value={filters.type || ''}
                  onChange={handleFilterTypeChange}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button variant="bordered-primary" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <div className="bg-light rounded-lg border border-grey_light overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-grey_light">
              <thead className="bg-bg_grey_light">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-dark uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-dark uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-dark uppercase tracking-wider">
                    Description
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-dark uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-dark uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-light divide-y divide-grey_light">
                {isLoadingTransactions ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-grey_dark">
                      <span className="text-sm">Loading transactions...</span>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-grey_dark">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-bg_grey_light rounded-full flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-grey_dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-medium text-dark mb-2">No transactions found</h4>
                        <p className="text-sm text-grey_dark">
                          {searchQuery || filters.type
                            ? 'Try adjusting your filters or search query'
                            : 'No credit transactions yet'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-bg_grey_light transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-dark">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-dark font-medium">
                        {transaction.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-grey_dark max-w-xs truncate">
                        {transaction.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getTypeBadge(transaction.type)}`}>
                          {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={transaction.type === 'credit' ? 'text-primary font-medium' : 'text-danger font-medium'}>
                          {transaction.type === 'credit' ? '+' : '-'}{transaction.amount.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          {filteredTransactions.length > 0 && (
            <div className="bg-bg_grey_light px-6 py-3 border-t border-grey_light">
              <div className="flex flex-col sm:flex-row justify-between items-center">
                <p className="text-sm text-grey_dark mb-2 sm:mb-0">
                  Showing <span className="font-medium text-dark">{filteredTransactions.length}</span> transaction{filteredTransactions.length !== 1 ? 's' : ''}
                </p>

                {transactionsData && transactionsData.total_pages > 1 && (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-dark">
                      Page {transactionsData.current_page} of {transactionsData.total_pages}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-light rounded-lg p-6 border border-grey_light">
            <h4 className="text-lg font-medium text-dark mb-4">How Credit Points Work</h4>
            <ul className="space-y-3 text-grey_dark">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 text-sm font-medium">1</span>
                </div>
                <span>1 credit point = $0.01 USD value for services</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 text-sm font-medium">2</span>
                </div>
                <span>Purchase points in bulk for discounted rates</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 text-sm font-medium">3</span>
                </div>
                <span>Points never expire and can be used for any service</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 text-sm font-medium">4</span>
                </div>
                <span>Get bonus points on large purchases and referrals</span>
              </li>
            </ul>
            <Button
              variant="google"
              onClick={() => console.log('Learn more clicked')}
              className="mt-4 text-primary hover:text-primary_variant"
            >
              Learn more about credit points →
            </Button>
          </div>

          <div className="bg-light rounded-lg p-6 border border-grey_light">
            <h4 className="text-lg font-medium text-dark mb-4">Quick Actions</h4>
            <div className="space-y-3">
              <Button
                variant="bordered-primary"
                onClick={() => navigate('/credit/purchase')}
                className="w-full flex items-center gap-3 justify-start px-4"
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <img src={plusIcon} alt="Purchase" className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-dark">Purchase Points</div>
                  <div className="text-xs text-grey_dark">Add more credits to your account</div>
                </div>
              </Button>

              <Button
                variant="bordered-primary"
                onClick={() => navigate('/credit/transfer')}
                className="w-full flex items-center gap-3 justify-start px-4"
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-medium text-dark">Transfer Points</div>
                  <div className="text-xs text-grey_dark">Share credits with team members</div>
                </div>
              </Button>

              <Button
                variant="bordered-primary"
                onClick={() => navigate('/credit/settings')}
                className="w-full flex items-center gap-3 justify-start px-4"
              >
                <div className="w-8 h-8 bg-bg_grey_light rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-medium text-dark">Settings</div>
                  <div className="text-xs text-grey_dark">Manage credit preferences</div>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPointsPage;
